import { useEffect, useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { REANALYZE_OPTIONS } from '@/lib/reanalyzeOptions';

// Multi-select picker for which fields to re-extract. Used on the
// ModerationDashboard (batch across the corpus) and the StoryViewPage
// (one story). Same UX, one component — adding a new option in
// lib/reanalyzeOptions propagates everywhere.
//
// Semantics:
//   • Checkbox per option in REANALYZE_OPTIONS.
//   • Apply fires onApply({ fields, label }) with the UNION of every
//     checked option's `fields` array and a human-readable label
//     joined from the checked option labels.
//   • Selection state resets each time the popover opens — operators
//     should pick afresh per invocation, not inherit stale ticks.
//   • Disabled while the parent is mid-run (parent passes `running`).
export default function ReanalyzeFieldsPopover({
  onApply,
  running = false,
  // Extra disable signal that does NOT swap the label to "Reanalyzing…"
  // — used when a sibling action (e.g. backfill embeddings) is in
  // flight and we just want to block the popover from opening.
  disabled = false,
  runningLabel = null,
  triggerLabel = 'Reanalyze field…',
  triggerTestId = 'reanalyze-btn',
  buttonClassName = '',
  align = 'end',
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => new Set());

  // Reset every time the popover opens so old ticks don't linger.
  useEffect(() => {
    if (open) setPicked(new Set());
  }, [open]);

  const toggle = (label) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleApply = () => {
    if (running) return;
    const chosen = REANALYZE_OPTIONS.filter((o) => picked.has(o.label));
    if (chosen.length === 0) return;
    // Union of all fields across checked options. Set dedups when
    // options share columns (e.g. Practices spans 3, none right now
    // overlap with others — but safer to dedupe regardless).
    const fields = Array.from(
      new Set(chosen.flatMap((o) => o.fields)),
    );
    const label = chosen.map((o) => o.label).join(' + ');
    // True if any checked option asks to rebuild the semantic-chunk lane.
    const rebuildChunks = chosen.some((o) => o.rebuildChunks);
    setOpen(false);
    onApply({ fields, label, rebuildChunks });
  };

  const pickedCount = picked.size;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={running || disabled}
          className={buttonClassName}
          data-testid={triggerTestId}
          title="Re-run Gemini extraction for the fields you tick. Other columns (including manually-curated tags) are preserved."
        >
          <Sparkles className={`h-4 w-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
          {running ? (runningLabel || 'Reanalyzing…') : triggerLabel}
          {!running && <ChevronDown className="h-4 w-4 ml-1.5 opacity-70" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-0">
        <div className="px-3 py-2 border-b border-[#E6E2D6] bg-[#FBF4EB]">
          <p className="text-xs uppercase tracking-wide text-[#6B665E] font-medium">
            Refresh fields — others preserved
          </p>
        </div>
        <ul className="py-1 max-h-72 overflow-y-auto">
          {REANALYZE_OPTIONS.map(({ label, fields }) => {
            const checked = picked.has(label);
            return (
              <li key={label}>
                <label
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#FBF4EB]"
                  data-testid={`reanalyze-option-${fields[0]}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(label)}
                    aria-label={label}
                  />
                  <span className="text-sm text-[#2D2A26] flex-1">{label}</span>
                  <span className="text-[10px] text-[#6B665E] truncate max-w-[80px]">
                    {fields.length > 1 ? `${fields.length} cols` : fields[0]}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-[#E6E2D6] px-3 py-2 flex items-center justify-between gap-2">
          <span className="text-xs text-[#6B665E]">
            {pickedCount === 0 ? 'Pick at least one' : `${pickedCount} selected`}
          </span>
          <Button
            type="button"
            size="sm"
            disabled={pickedCount === 0}
            onClick={handleApply}
            className="bg-[#C05621] hover:bg-[#A0461A] text-white"
            data-testid="reanalyze-apply-btn"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
